import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { useToast } from '../components/Toast';
import { apiFetch } from '../api';
import InAppNotificationModal, { InAppNotification } from '../components/InAppNotificationModal';

interface ChatContextType {
  socket: Socket | null;
  unreadCount: number;
  onlineUsers: Set<number>;
  activeRoomId: number | null;
  setActiveRoomId: (id: number | null) => void;
  refreshUnreadCount: () => Promise<void>;
  playNotificationSound: () => void;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export function useChat() {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
}

/**
 * Prüft, ob die aktuelle Seite eine externe/öffentliche Seite ist (z.B. Öffentliche Kursbuchung oder Helfer-Plattform).
 * Auf diesen Seiten dürfen keine internen Popups/Toasts aus Meldewesen, Ereignismeldungen oder Chat angezeigt werden.
 */
export function isExternalPublicRoute(pathname: string = window.location.pathname): boolean {
  return (
    pathname.startsWith('/courses-events/public') ||
    pathname.startsWith('/helfer-plattform') ||
    pathname.startsWith('/public/tagesplan') ||
    pathname.startsWith('/license-scan') ||
    pathname.startsWith('/onboarding/portal') ||
    pathname.startsWith('/customer-display') ||
    pathname.startsWith('/login') ||
    pathname.startsWith('/setup-2fa')
  );
}

function getCurrentUserId(): number | null {
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    const cleanToken = token.startsWith('Bearer ') ? token.slice(7) : token;
    const payloadBase64 = cleanToken.split('.')[1];
    if (!payloadBase64) return null;
    const payloadJson = atob(payloadBase64.replace(/-/g, '+').replace(/_/g, '/'));
    const payload = JSON.parse(payloadJson);
    return payload?.id || payload?.userId || null;
  } catch {
    return null;
  }
}

export function playNotificationSound() {
  try {
    if (isExternalPublicRoute()) {
      return;
    }
    if (localStorage.getItem('chat_sound_muted') === 'true') {
      return;
    }

    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextClass) return;
    const ctx = new AudioContextClass();

    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }

    const now = ctx.currentTime;

    // Tone 1: 698.46 Hz (F5), gentle chime attack
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(698.46, now);
    gain1.gain.setValueAtTime(0, now);
    gain1.gain.linearRampToValueAtTime(0.2, now + 0.02);
    gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.28);
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.start(now);
    osc1.stop(now + 0.28);

    // Tone 2: 1046.5 Hz (C6), clear and bright
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(1046.5, now + 0.12);
    gain2.gain.setValueAtTime(0, now + 0.12);
    gain2.gain.linearRampToValueAtTime(0.25, now + 0.14);
    gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.48);
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.start(now + 0.12);
    osc2.stop(now + 0.48);
  } catch (e) {
    console.warn('Could not play notification sound:', e);
  }
}

export function ChatProvider({ children }: { children: React.ReactNode }) {
  const [unreadCount, setUnreadCount] = useState(0);
  const [onlineUsers, setOnlineUsers] = useState<Set<number>>(new Set());
  const [activeRoomId, setActiveRoomId] = useState<number | null>(null);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [authToken, setAuthToken] = useState<string | null>(() => localStorage.getItem('token'));
  const [isExternalPage, setIsExternalPage] = useState<boolean>(() => isExternalPublicRoute());

  const socketRef = useRef<Socket | null>(null);
  const activeRoomIdRef = useRef<number | null>(null);
  const heartbeatRef = useRef<any>(null);
  const handledMessageIdsRef = useRef<Set<number>>(new Set());
  const dismissedKeysRef = useRef<Set<string>>(new Set());
  const syncChannelRef = useRef<BroadcastChannel | null>(null);
  const toast = useToast();

  // Update ref whenever state changes
  useEffect(() => {
    activeRoomIdRef.current = activeRoomId;
  }, [activeRoomId]);

  // Keep authToken and external route detection synced
  useEffect(() => {
    const checkState = () => {
      const currentToken = localStorage.getItem('token');
      if (currentToken !== authToken) {
        setAuthToken(currentToken);
      }
      const ext = isExternalPublicRoute();
      setIsExternalPage(prev => (prev !== ext ? ext : prev));
    };

    const interval = setInterval(checkState, 1000);
    window.addEventListener('storage', checkState);
    window.addEventListener('popstate', checkState);

    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', checkState);
      window.removeEventListener('popstate', checkState);
    };
  }, [authToken]);

  const refreshUnreadCount = useCallback(async () => {
    try {
      if (isExternalPublicRoute()) return;
      const token = localStorage.getItem('token');
      if (!token) {
        setUnreadCount(0);
        return;
      }
      const rooms = await apiFetch('/chat/rooms');
      if (Array.isArray(rooms)) {
        const total = rooms.reduce((sum: number, room: any) => sum + (room.unread_count || 0), 0);
        setUnreadCount(total);
      }
    } catch (e) {
      // Ignore network errors silently
    }
  }, []);

  const [pendingModals, setPendingModals] = useState<InAppNotification[]>([]);

  const applyDismissalLocally = useCallback((payload: { id?: number | string; notificationId?: number | string; title?: string; message?: string }) => {
    const targetId = payload.notificationId ?? payload.id;
    if (targetId !== undefined && targetId !== null) {
      dismissedKeysRef.current.add(`id:${targetId}`);
    }
    if (payload.title && payload.message) {
      dismissedKeysRef.current.add(`content:${payload.title}:::${payload.message}`);
    }

    setPendingModals(prev =>
      prev.filter(m => {
        if (targetId !== undefined && targetId !== null && m.id !== undefined && String(m.id) === String(targetId)) {
          return false;
        }
        if (payload.title && payload.message && m.title === payload.title && m.message === payload.message) {
          return false;
        }
        return true;
      })
    );

    toast.dismissNotificationToasts({
      notificationId: targetId,
      title: payload.title,
      message: payload.message,
    });
  }, [toast]);

  // Cross-tab BroadcastChannel & storage listener for dismissing modals/toasts across all open tabs
  useEffect(() => {
    let bc: BroadcastChannel | null = null;
    try {
      if (typeof BroadcastChannel !== 'undefined') {
        bc = new BroadcastChannel('orgapilot_inapp_sync');
        syncChannelRef.current = bc;
        bc.onmessage = (event) => {
          if (event?.data) {
            applyDismissalLocally(event.data);
          }
        };
      }
    } catch {}

    const handleStorageSync = (e: StorageEvent) => {
      if (e.key === 'orgapilot_dismiss_sync' && e.newValue) {
        try {
          const data = JSON.parse(e.newValue);
          applyDismissalLocally(data);
        } catch {}
      }
    };
    window.addEventListener('storage', handleStorageSync);

    return () => {
      if (bc) {
        try { bc.close(); } catch {}
      }
      syncChannelRef.current = null;
      window.removeEventListener('storage', handleStorageSync);
    };
  }, [applyDismissalLocally]);

  const broadcastAndDismissModal = useCallback((notif: InAppNotification) => {
    const payload = {
      id: notif.id,
      notificationId: notif.id,
      title: notif.title,
      message: notif.message,
      ts: Date.now(),
    };
    applyDismissalLocally(payload);

    try {
      syncChannelRef.current?.postMessage(payload);
    } catch {}
    try {
      localStorage.setItem('orgapilot_dismiss_sync', JSON.stringify(payload));
    } catch {}
  }, [applyDismissalLocally]);

  const fetchUnreadInApp = useCallback(async () => {
    try {
      if (isExternalPublicRoute()) {
        setPendingModals([]);
        return;
      }
      const data = await apiFetch('/system-notifications/inapp/unread');
      if (Array.isArray(data)) {
        const modals = data.filter((n: any) => {
          if (n.type !== 'modal') return false;
          if (n.id !== undefined && dismissedKeysRef.current.has(`id:${n.id}`)) return false;
          if (n.title && n.message && dismissedKeysRef.current.has(`content:${n.title}:::${n.message}`)) return false;
          return true;
        });
        setPendingModals(modals);
      }
    } catch {}
  }, []);

  // Periodic polling fallback (every 10s) to guarantee badge freshness even if WebSocket packet is dropped
  useEffect(() => {
    if (!authToken) return;

    refreshUnreadCount();
    const pollInterval = setInterval(() => {
      refreshUnreadCount();
    }, 10000);

    const handleFocus = () => {
      refreshUnreadCount();
      fetchUnreadInApp();
    };
    window.addEventListener('focus', handleFocus);

    return () => {
      clearInterval(pollInterval);
      window.removeEventListener('focus', handleFocus);
    };
  }, [authToken, refreshUnreadCount, fetchUnreadInApp]);

  // WebSocket Connection Management (tied to authToken)
  useEffect(() => {
    if (!authToken) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setSocket(null);
      }
      return;
    }

    const socketUrl = window.location.hostname === 'localhost'
      ? `http://${window.location.hostname}:3000`
      : window.location.origin;

    const s = io(`${socketUrl}/chat`, {
      path: '/socket.io/',
      transports: ['polling', 'websocket'],
      auth: { token: authToken },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: Infinity,
      timeout: 20000,
    });

    socketRef.current = s;
    setSocket(s);

    const startHeartbeat = () => {
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      heartbeatRef.current = setInterval(() => {
        if (s.connected) {
          s.emit('heartbeat');
        }
      }, 25000);
    };

    s.on('connect', () => {
      console.log('✅ Chat WebSocket connected');
      startHeartbeat();
      refreshUnreadCount();
    });

    s.on('reconnect', () => {
      console.log('🔄 Chat WebSocket reconnected');
      startHeartbeat();
      refreshUnreadCount();
    });

    s.on('disconnect', (reason) => {
      console.log('⚠️ Chat WebSocket disconnected:', reason);
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      setOnlineUsers(new Set());
    });

    s.on('online_users', (userIds: number[]) => {
      setOnlineUsers(new Set(userIds));
    });

    s.on('user_presence', ({ userId, status }: { userId: number; status: 'online' | 'offline' }) => {
      setOnlineUsers(prev => {
        const next = new Set(prev);
        if (status === 'online') {
          next.add(userId);
        } else {
          next.delete(userId);
        }
        return next;
      });
    });

    // Handle new incoming message
    const handleNewMessage = (msg: any) => {
      if (!msg || !msg.id) return;
      if (isExternalPublicRoute()) return;

      // Deduplicate in case message is broadcast to room and user channels
      if (handledMessageIdsRef.current.has(msg.id)) return;
      handledMessageIdsRef.current.add(msg.id);
      if (handledMessageIdsRef.current.size > 200) {
        const arr = Array.from(handledMessageIdsRef.current);
        handledMessageIdsRef.current = new Set(arr.slice(-100));
      }

      const myId = getCurrentUserId();
      // Skip notifications if the message was sent by the current user
      if (myId && msg.sender_id === myId) {
        return;
      }

      // 1. Play sound notification
      playNotificationSound();

      // 2. If user is not currently inside that specific room on /chat, display toast
      const isChatPage = window.location.pathname === '/chat';
      const isCurrentActiveRoom = activeRoomIdRef.current !== null && msg.room_id === activeRoomIdRef.current;

      if (!isChatPage || !isCurrentActiveRoom) {
        toast.info(`Neue Nachricht von ${msg.sender_name || 'Benutzer'}: ${msg.message || ''}`);
      }

      // 3. Immediately refresh unread count so the red dot updates instantly
      refreshUnreadCount();
    };

    s.on('new_message', handleNewMessage);

    s.on('unread_count_update', () => {
      refreshUnreadCount();
    });

    s.on('messages_read', () => {
      refreshUnreadCount();
    });

    const handleInAppNotification = (notif: InAppNotification) => {
      if (!notif) return;
      // Auf externen Seiten (Kurse, Helfer-Plattform etc.) niemals interne Popups/Toasts anzeigen!
      if (isExternalPublicRoute()) return;

      if (notif.id !== undefined && dismissedKeysRef.current.has(`id:${notif.id}`)) return;
      if (notif.title && notif.message && dismissedKeysRef.current.has(`content:${notif.title}:::${notif.message}`)) return;

      if (notif.sound !== false) {
        playNotificationSound();
      }

      if (notif.type === 'modal') {
        setPendingModals(prev => {
          if (notif.id !== undefined && prev.some(m => m.id === notif.id)) return prev;
          return [...prev, notif];
        });
      } else {
        const sev = notif.severity || 'info';
        toast.showToast(notif.message, sev as any, notif.title, notif.id);
      }
    };

    const handleInAppNotificationDismissed = (payload: { id?: number | string; title?: string; message?: string }) => {
      if (!payload) return;
      applyDismissalLocally(payload);
    };

    s.on('inapp_notification', handleInAppNotification);
    s.on('inapp_notification_dismissed', handleInAppNotificationDismissed);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && s.connected) {
        s.emit('request_online_users');
        refreshUnreadCount();
        fetchUnreadInApp();
      } else if (document.visibilityState === 'visible' && !s.connected) {
        s.connect();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    refreshUnreadCount();
    fetchUnreadInApp();

    return () => {
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      s.off('new_message', handleNewMessage);
      s.off('inapp_notification', handleInAppNotification);
      s.off('inapp_notification_dismissed', handleInAppNotificationDismissed);
      s.disconnect();
      socketRef.current = null;
      setSocket(null);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [authToken, refreshUnreadCount, fetchUnreadInApp, toast, applyDismissalLocally]);

  const shouldRenderModal = !isExternalPage && !isExternalPublicRoute() && pendingModals.length > 0;

  return (
    <ChatContext.Provider
      value={{
        socket,
        unreadCount,
        onlineUsers,
        activeRoomId,
        setActiveRoomId,
        refreshUnreadCount,
        playNotificationSound,
      }}
    >
      {children}
      {shouldRenderModal && (
        <InAppNotificationModal
          notification={pendingModals[0]}
          onDismiss={() => {
            broadcastAndDismissModal(pendingModals[0]);
          }}
        />
      )}
    </ChatContext.Provider>
  );
}
